/**
 * Turning what the counter is looking at into what the roll prints.
 *
 * The API talks in rupees; the receipt layout in `@kortex/shared` works in
 * paise like the rest of the domain, so the conversion happens here, once,
 * rather than in the layout or on the screen.
 */
import { toPaise, type ReceiptData, type ReceiptItem } from '@kortex/shared';
import type { Invoice } from '../components/InvoiceDoc';
import { date, time } from './format';

const paise = (value: number | string | null | undefined) => toPaise(value ?? 0);

const stamp = (value: string) => `${date(value)}, ${time(value)}`;

/** Where the order is going, in the words the kitchen and the guest both use. */
function destination(input: {
  tableCode?: string | null;
  roomNumber?: string | null;
  type?: string | null;
}): string | undefined {
  if (input.tableCode) return `Table ${input.tableCode}`;
  if (input.roomNumber) return `Room ${input.roomNumber}`;
  if (input.type) return input.type.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  return undefined;
}

/** The GST bill a guest is handed, on a till roll instead of A4. */
export function invoiceReceipt(invoice: Invoice): ReceiptData {
  const items: ReceiptItem[] = invoice.lines.map((line) => ({
    name: line.description,
    quantity: Number(line.quantity),
    unitPrice: paise(line.unitPrice),
    amount: paise(line.total),
    gstRate: Number(line.gstRate),
  }));

  return {
    property: {
      name: invoice.property.name,
      address: invoice.property.address,
      city: invoice.property.city,
      pincode: invoice.property.pincode,
      gstin: invoice.property.gstin,
      phone: invoice.property.phone,
      fssai: invoice.property.fssai,
    },
    title: invoice.status === 'VOID' ? 'TAX INVOICE (VOID)' : 'TAX INVOICE',
    number: invoice.number,
    issuedAt: stamp(invoice.issuedAt),
    guestName: invoice.guestName,
    orderType: destination({ tableCode: invoice.tableCode, roomNumber: invoice.roomNumber }),
    paymentMode: invoice.mode,
    items,
    offers: (invoice.offers ?? []).map((offer) => ({
      name: offer.name,
      discount: paise(offer.discount),
    })),
    totals: {
      subtotal: paise(invoice.subtotal),
      discount: paise(invoice.discount),
      cgst: paise(invoice.cgst),
      sgst: paise(invoice.sgst),
      igst: paise(invoice.igst),
      roundOff: paise(invoice.roundOff),
      total: paise(invoice.total),
    },
  };
}

export interface KotTicket {
  number: string;
  type: string;
  openedAt: string;
  tableCode: string | null;
  roomNumber: string | null;
  guestName: string | null;
  lines: Array<{ id: string; name: string; quantity: number; unitPrice: number; amount: number }>;
}

/** The slip the kitchen cooks from. Property name only: no GSTIN, no money. */
export function ticketKot(ticket: KotTicket, propertyName: string): ReceiptData {
  return {
    property: {
      name: propertyName,
      address: '',
      city: '',
      pincode: null,
      gstin: '',
      phone: '',
      fssai: null,
    },
    title: 'KOT',
    number: ticket.number,
    issuedAt: stamp(ticket.openedAt),
    guestName: ticket.guestName ?? undefined,
    orderType: destination(ticket),
    items: ticket.lines.map((line) => ({
      name: line.name,
      quantity: Number(line.quantity),
      unitPrice: paise(line.unitPrice),
      amount: paise(line.amount),
    })),
    totals: { subtotal: 0, discount: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, total: 0 },
  };
}
